import { ApiService } from './ApiService';
import { Result, ResultOf } from '@/types/result';
import {
    PaidFeatureType,
    PaidFeaturesPage,
    PaidFeaturePurchaseResult,
    CancelPaidFeatureModel,
    SubscriptionPackageType,
    PackagesPage,
    PackagePurchaseResult,
    CancelPackageModel,
} from '@/pages/PaidFeatures/types/PaidFeaturesTypes';

// ========== Новые типы для модалок тарифов ==========

/**
 * Тип модалки о новых тарифах
 */
export enum PaidTariffModalType {
    /** Анонс новых тарифов (день релиза) */
    ReleaseAnnouncement = 1,
    /** Скоро заканчивается триал */
    TrialExpiring = 2,
    /** Истекает PRO-подписка */
    ProExpiring = 3,
    /** Не удалось списать оплату за PRO и истекает оплаченное время */
    ProPaymentFailed = 4,
    /** Триал закончился (доступ ко всем функциям завершён, апселл новых тарифов) */
    TrialExpired = 5,
    /** Попробуйте триал */
    TryTrial = 6,
    /** Пауза PRO скоро завершится (окно возврата ещё идёт) */
    PauseEndingSoon = 7,
    /** Подписка PRO возобновлена после паузы */
    PauseResumed = 8,
    /** Последний шанс оставить PRO — авто-возврат по окончании паузы не удался (grace), повод «в день окончания» */
    PauseFinalChance = 9,
    /** Пауза PRO скоро завершится — повод «за 1 день» (та же модалка, что PauseEndingSoon) */
    PauseEndingSoonLastDay = 10,
    /** Последний шанс оставить PRO — повод «в конце grace» (та же модалка, что PauseFinalChance) */
    PauseGraceEnding = 11,
}

/**
 * Ответ метода GetModalInfo
 */
export interface PaidTariffModalInfoResponse {
    /** Нужно ли показать модалку */
    shouldShow: boolean;
    /** Тип модалки (определяет текст и оформление) */
    modalType?: PaidTariffModalType;
    /** Количество дней до окончания триала (для типа TrialExpiring) */
    trialDaysRemaining?: number;
    /** Дата окончания PRO (для типа ProExpiring) */
    proExpiresAt?: string; // ISO-строка с датой
    /** Дата окончания бесплатного доступа после релиза (для типа ReleaseAnnouncement) */
    freeAccessUntil?: string; // ISO-строка с датой
    /** Триал уже истёк (для типа ReleaseAnnouncement — меняет текст модалки) */
    isTrialExpired?: boolean;
    /** Дата окончания окна возврата из паузы (для типа PauseEndingSoon) */
    pauseEndsAt?: string; // ISO-строка с датой
    /** Дата перевода на бесплатный тариф по окончании grace авто-возврата (для типа PauseFinalChance) */
    transferToFreeAt?: string; // ISO-строка с датой
}

// ========== Основной API ==========

export class PaidFeaturesApi extends ApiService {
    base = 'PaidFeatures';

    async getInfo(): Promise<ResultOf<PaidFeaturesPage>> {
        return await this.get(this.getUrl('GetInfo'));
    }

    async buy(featureType: PaidFeatureType): Promise<ResultOf<PaidFeaturePurchaseResult>> {
        return await this.post(this.getUrl('Buy'), { featureType });
    }

    async cancelAutoRenewal(model: CancelPaidFeatureModel): Promise<Result> {
        return await this.post(this.getUrl('CancelAutoRenewal'), model);
    }

    async restoreAutoRenewal(featureType: PaidFeatureType): Promise<Result> {
        return await this.post(this.getUrl('RestoreAutoRenewal'), { featureType });
    }

    async hasFeature(featureType: PaidFeatureType, boardId?: number, chatId?: number): Promise<boolean> {
        const params: Record<string, number> = { featureType };
        if (boardId != null) params.boardId = boardId;
        if (chatId != null) params.chatId = chatId;
        const result: ResultOf<{ value: string }> = await this.get(this.getUrl('HasFeature'), params);
        return result.isSuccess && result.entity?.value === 'true';
    }

    async retryCharge(): Promise<ResultOf<PaidFeaturePurchaseResult>> {
        return await this.post(this.getUrl('RetryCharge'), {});
    }

    // ========== Новые методы ==========

    /**
     * Получить информацию о модалке новых тарифов для показа при входе
     */
    async getModalInfo(): Promise<ResultOf<PaidTariffModalInfoResponse>> {
        return await this.get(this.getUrl('GetModalInfo'));
    }

    /**
     * Пометить модалку о новых тарифах как показанную
     * @param modalType Тип модалки, которую нужно скрыть
     */
    async dismissNewTariffModal(modalType: PaidTariffModalType): Promise<Result> {
        return await this.post(this.getUrl('DismissNewTariffModal'), { modalType });
    }

    // ========== Пакеты «Оптима»/«Профи» (FINCH-2410) ==========

    /**
     * Получить данные секции пакетов: карточки, % выгоды, состояния кнопок
     */
    async getPackages(): Promise<ResultOf<PackagesPage>> {
        return await this.get(this.getUrl('GetPackagesInfo'));
    }

    /**
     * Купить/сменить пакет.
     * Если карта привязана — charge немедленно; иначе вернётся URL формы оплаты.
     * @param type Тип пакета (Оптима/Профи)
     * @param durationDays Длительность цикла в днях (90 / 180 / 360)
     */
    async buyPackage(type: SubscriptionPackageType, durationDays: number): Promise<ResultOf<PackagePurchaseResult>> {
        return await this.post(this.getUrl('BuyPackage'), { type, durationDays });
    }

    /**
     * Отменить автопродление пакета (доступ сохраняется до конца оплаченного периода)
     */
    async cancelPackage(model: CancelPackageModel): Promise<Result> {
        return await this.post(this.getUrl('CancelPackageAutoRenewal'), model);
    }

    /**
     * Возобновить автопродление пакета
     */
    async restorePackage(): Promise<Result> {
        return await this.post(this.getUrl('RestorePackageAutoRenewal'), {});
    }

    /**
     * Повторить списание за активный пакет при ошибке автопродления
     */
    async retryPackageCharge(): Promise<ResultOf<PackagePurchaseResult>> {
        return await this.post(this.getUrl('RetryPackageCharge'), {});
    }
}
